import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FISERV_CONFIG, FiservConfig } from '../fiserv.config';
import { FiservSavedCard } from '../models/fiserv.models';

export interface FiservSaveCardRequest {
  cardNumber: string;      // raw card number — tokenized server-side
  expiry: string;          // MMYY e.g. "1226"
  cardholderName?: string;
  customerId: string;      // patientId as string
}

export interface FiservSaveCardResult {
  id: number;
  customerId: string;
  gatewayPaymentMethodId: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isActive: boolean;
}

@Injectable()
export class FiservPaymentMethodService {
  private readonly base: string;

  constructor(
    private http: HttpClient,
    @Inject(FISERV_CONFIG) config: FiservConfig
  ) {
    this.base = config.apiUrl.replace(/\/$/, '');
  }

  /**
   * Returns saved cards for a patient.
   * Calls GET /api/v1/FiservTerminal/GetSavedCards?patientId={patientId}
   * via the epatientlink bridge.
   * Returns empty array on error — non-critical.
   */
  getByCustomer(patientId: number): Observable<FiservSavedCard[]> {
    return this.http
      .get<FiservSavedCard[]>(
        `${this.base}/api/v1/FiservTerminal/GetSavedCards`,
        { params: { patientId: patientId.toString() } }
      )
      .pipe(
        catchError(() => of([]))  // fail silently — return empty array
      );
  }

  /**
   * Tokenize a raw card number via CardPointe cardsecure then save to DB.
   * Calls POST /api/v1/FiservTerminal/SaveCard
   */
  saveCard(req: FiservSaveCardRequest): Observable<FiservSaveCardResult> {
    return this.http.post<FiservSaveCardResult>(
      `${this.base}/api/v1/FiservTerminal/SaveCard`, req
    );
  }

  /**
   * Deactivate a saved card by DB id.
   * Calls DELETE /api/v1/FiservTerminal/DeleteCard/{id}
   */
  deleteCard(id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/api/v1/FiservTerminal/DeleteCard/${id}`
    );
  }

  /**
   * Send an email to the patient with a link to register their card.
   * Calls POST /api/v1/FiservTerminal/SendAddCardLink
   */
  sendAddCardLink(
    patientId: number,
    branchId: number,
    companyId: number
  ): Observable<void> {
    return this.http.post<void>(
      `${this.base}/api/v1/FiservTerminal/SendAddCardLink`,
      { patientId, branchId, companyId }
    );
  }
}
